import { useState, useRef } from "react";
import { supabase } from "../supabase";

export default function VoiceRecorder({ onTranscription, userId, projectName }) {
  const [state, setState] = useState("idle"); // idle | recording | uploading | transcribing | error
  const [error, setError] = useState(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  const start = async () => {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : "audio/webm";
      const recorder = new MediaRecorder(stream, { mimeType });
      chunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.start();
      mediaRecorderRef.current = recorder;
      setState("recording");
    } catch {
      setError("Microphone non autorisé. Vérifiez les permissions.");
      setState("error");
    }
  };

  const stop = () => {
    const recorder = mediaRecorderRef.current;
    if (!recorder) return;
    recorder.onstop = async () => {
      recorder.stream.getTracks().forEach(t => t.stop());
      const blob = new Blob(chunksRef.current, { type: "audio/webm" });

      setState("uploading");
      try {
        // Upload dans Supabase Storage
        const filename = `${userId || "anon"}-${Date.now()}.webm`;
        const { data: uploadData, error: uploadErr } = await supabase.storage
          .from("voice-memos")
          .upload(filename, blob, { contentType: "audio/webm", upsert: false });

        if (uploadErr) throw new Error(`Upload: ${uploadErr.message}`);

        const { data: { publicUrl } } = supabase.storage.from("voice-memos").getPublicUrl(uploadData.path);

        setState("transcribing");

        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;

        const FUNCTION_URL = `${supabase.supabaseUrl}/functions/v1/transcribe-audio`;
        const fnResponse = await fetch(FUNCTION_URL, {
          method: "POST",
          headers: {
            "Content-Type": "audio/webm",
            "apikey": supabase.supabaseKey,
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: blob,
        });

        if (!fnResponse.ok) {
          const err = await fnResponse.text();
          throw new Error(`Transcription: ${err}`);
        }

        const fnData = await fnResponse.json();
        if (fnData.error) throw new Error(fnData.error);

        onTranscription(fnData.summary || fnData.transcript || "", publicUrl);
        setState("idle");
      } catch (e) {
        setError(e.message || "Erreur lors de la transcription.");
        setState("error");
      }
    };
    recorder.stop();
  };

  if (state === "recording") {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 9 }}>
        <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#FC8181", display: "inline-block", animation: "pulse 1s infinite" }} />
        <span style={{ fontSize: 12, color: "#FC8181" }}>Enregistrement en cours…</span>
        <button onClick={stop} className="btn"
          style={{ marginLeft: "auto", fontSize: 11, padding: "5px 12px", borderRadius: 7, background: "#FC818120", border: "1px solid #FC818140", color: "#FC8181" }}>
          ⏹ Stop
        </button>
      </div>
    );
  }

  if (state === "uploading" || state === "transcribing") {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 9, fontSize: 12, color: "#C9A96E" }}>
        <span style={{ display: "inline-block", width: 12, height: 12, border: "2px solid #C9A96E40", borderTopColor: "#C9A96E", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
        {state === "uploading" ? "Upload en cours…" : "Transcription en cours…"}
      </div>
    );
  }

  return (
    <div style={{ marginBottom: 9 }}>
      <button onClick={start} className="btn"
        style={{ fontSize: 12, padding: "7px 14px", borderRadius: 8, background: "#1A1916", border: "1px solid #2A2924", color: "#C9A96E", display: "flex", alignItems: "center", gap: 7 }}>
        🎤 Enregistrer un mémo vocal
      </button>
      {state === "error" && (
        <div style={{ marginTop: 6, fontSize: 11, color: "#FC8181" }}>{error}</div>
      )}
    </div>
  );
}
